import type { IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import type {
  ArcEntity,
  CircleEntity,
  EllipseEntity,
} from '@/plugins/cad/module/CADModule/model/entity'

const TWO_PI = Math.PI * 2

/**
 * 返回仅包含 x/y 的点副本，不保留 z。
 *
 * @param point - 源点
 * @returns 新的平面点对象
 */
export function clonePoint(point: IPoint): IPoint {
  return { x: point.x, y: point.y }
}

/**
 * 计算两点的平面欧氏距离。
 *
 * @param p1 - 第一个点
 * @param p2 - 第二个点
 * @returns 距离值，单位与输入坐标相同
 */
export function distanceBetweenPoints(p1: IPoint, p2: IPoint): number {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y)
}

/**
 * 将角度归一化到 [0, 2π)。
 *
 * @param angle - 原始角度，单位弧度
 * @returns 归一化后的角度，单位弧度
 */
export function normalizeAngle(angle: number): number {
  let result = angle % TWO_PI
  if (result < 0) {
    result += TWO_PI
  }
  return result
}

/**
 * 根据圆心、半径与角度求圆上一点。
 *
 * @param center - 圆心
 * @param radius - 半径，单位世界坐标
 * @param angle - 角度，单位弧度，0 指向 +X
 * @returns 圆上的坐标点
 */
export function getPointByAngle(center: IPoint, radius: number, angle: number): IPoint {
  return {
    x: center.x + radius * Math.cos(angle),
    y: center.y + radius * Math.sin(angle),
  }
}

/**
 * 计算圆弧中点角，考虑顺逆时针方向。
 *
 * @param entity - 圆弧实体
 * @returns 中点对应的角度，已归一化到 [0, 2π)
 */
export function getArcMidAngle(entity: ArcEntity): number {
  const start = normalizeAngle(entity.startAngle)
  const end = normalizeAngle(entity.endAngle)
  if (entity.clockwise) {
    let delta = start - end
    if (delta < 0) {
      delta += TWO_PI
    }
    return normalizeAngle(start - delta / 2)
  }

  let delta = end - start
  if (delta < 0) {
    delta += TWO_PI
  }
  return normalizeAngle(start + delta / 2)
}

/**
 * 计算圆弧实体的三个夹点：起点、中点、终点。
 *
 * @param entity - 圆弧实体
 * @returns 含 start/mid/end 的坐标对象
 */
export function getArcGripPoints(entity: ArcEntity) {
  return {
    start: getPointByAngle(entity.center, entity.radius, entity.startAngle),
    end: getPointByAngle(entity.center, entity.radius, entity.endAngle),
    mid: getPointByAngle(entity.center, entity.radius, getArcMidAngle(entity)),
  }
}

/** 由三点确定的圆弧参数 */
export interface ArcThreePointParams {
  /** 圆心坐标 */
  center: IPoint
  /** 半径，单位世界坐标 */
  radius: number
  /** 起始角，单位弧度 */
  startAngle: number
  /** 终止角，单位弧度 */
  endAngle: number
  /** 是否顺时针扫描 */
  clockwise: boolean
}

/**
 * 由三点求圆弧参数。
 *
 * @param p1 - 起点
 * @param p2 - 中间点
 * @param p3 - 终点
 * @returns 圆弧参数；三点共线或过近时返回 null
 */
export function getArcParamsByThreePoints(
  p1: IPoint,
  p2: IPoint,
  p3: IPoint
): ArcThreePointParams | null {
  const x1 = p1.x,
    y1 = p1.y
  const x2 = p2.x,
    y2 = p2.y
  const x3 = p3.x,
    y3 = p3.y

  const a1 = x1 * x1 + y1 * y1
  const a2 = x2 * x2 + y2 * y2
  const a3 = x3 * x3 + y3 * y3

  const d = 2 * (x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2))
  if (Math.abs(d) < 1e-6) {
    return null
  }

  const centerX = (a1 * (y2 - y3) + a2 * (y3 - y1) + a3 * (y1 - y2)) / d
  const centerY = (a1 * (x3 - x2) + a2 * (x1 - x3) + a3 * (x2 - x1)) / d
  const center = { x: centerX, y: centerY }
  const radius = Math.sqrt((centerX - x1) ** 2 + (centerY - y1) ** 2)

  const startAngle = Math.atan2(p1.y - center.y, p1.x - center.x)
  let midAngle = Math.atan2(p2.y - center.y, p2.x - center.x)
  let endAngle = Math.atan2(p3.y - center.y, p3.x - center.x)

  const crossProduct = (p2.x - p1.x) * (p3.y - p2.y) - (p2.y - p1.y) * (p3.x - p2.x)
  const anticlockwise = crossProduct > 0

  if (anticlockwise) {
    while (midAngle < startAngle) midAngle += TWO_PI
    while (endAngle < midAngle) endAngle += TWO_PI
  } else {
    while (midAngle > startAngle) midAngle -= TWO_PI
    while (endAngle > midAngle) endAngle -= TWO_PI
  }

  return { center, radius, startAngle, endAngle, clockwise: !anticlockwise }
}

/**
 * 计算圆的四个象限点（上右下左）。
 *
 * @param entity - 圆实体
 * @returns 含 top/right/bottom/left 的坐标对象
 */
export function getCircleQuadrantPoints(entity: CircleEntity) {
  const { center, radius } = entity
  return {
    top: { x: center.x, y: center.y + radius },
    right: { x: center.x + radius, y: center.y },
    bottom: { x: center.x, y: center.y - radius },
    left: { x: center.x - radius, y: center.y },
  }
}

/**
 * 将二维向量绕原点旋转指定角度。
 *
 * @param vector - 待旋转向量
 * @param angle - 旋转角度，单位弧度，正值逆时针
 * @returns 旋转后的新向量
 */
export function rotateVector(vector: IPoint, angle: number): IPoint {
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  return {
    x: vector.x * cos - vector.y * sin,
    y: vector.x * sin + vector.y * cos,
  }
}

/**
 * 获取椭圆的半径与主次轴单位向量。
 *
 * @param entity - 椭圆实体
 * @returns 含 radiusX/radiusY/rotation/majorAxisUnit/minorAxisUnit 的描述对象
 */
export function getEllipseAxes(entity: EllipseEntity) {
  const radiusX = entity.radiusX
  const radiusY = entity.radiusY
  const rotation = entity.rotation
  const majorAxisUnit = rotateVector({ x: 1, y: 0 }, rotation)
  const minorAxisUnit = rotateVector({ x: 0, y: 1 }, rotation)

  return {
    radiusX,
    radiusY,
    rotation,
    majorAxisUnit,
    minorAxisUnit,
  }
}

/**
 * 计算椭圆四个象限点（沿主次轴正负方向）。
 *
 * @param entity - 椭圆实体
 * @returns 含 right/left/top/bottom 的坐标对象
 */
export function getEllipseQuadrantPoints(entity: EllipseEntity) {
  const { center } = entity
  const { radiusX, radiusY, majorAxisUnit, minorAxisUnit } = getEllipseAxes(entity)

  return {
    right: {
      x: center.x + majorAxisUnit.x * radiusX,
      y: center.y + majorAxisUnit.y * radiusX,
    },
    left: {
      x: center.x - majorAxisUnit.x * radiusX,
      y: center.y - majorAxisUnit.y * radiusX,
    },
    top: {
      x: center.x + minorAxisUnit.x * radiusY,
      y: center.y + minorAxisUnit.y * radiusY,
    },
    bottom: {
      x: center.x - minorAxisUnit.x * radiusY,
      y: center.y - minorAxisUnit.y * radiusY,
    },
  }
}

/**
 * 将点投影到以 origin 为原点、axisUnit 为单位方向的轴上，返回标量坐标。
 *
 * @param point - 被投影点
 * @param origin - 轴的原点
 * @param axisUnit - 轴的单位方向向量
 * @returns 投影标量，沿方向为正、反向为负
 */
export function projectPointToAxis(point: IPoint, origin: IPoint, axisUnit: IPoint): number {
  return (point.x - origin.x) * axisUnit.x + (point.y - origin.y) * axisUnit.y
}
